import { Badge, type BadgeProps } from '@ctw/shared/components/badge';
import { Button } from '@ctw/shared/components/button';
import { copyToClipboard } from '@ctw/shared/components/copy-to-clipboard';
import { HorizontalDivider } from '@ctw/shared/components/horizontal-divider';
import { notify } from '@ctw/shared/components/toast';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { pathWithParameters } from '@ctw/shared/utils/url';
import { type IconDefinition, faSquare } from '@fortawesome/pro-regular-svg-icons';
import { faCircleX, faClock, faLink, faSquareCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmpty, snakeCase } from 'lodash-es';
import {
  type MouseEvent,
  type PropsWithChildren,
  type ReactNode,
  type RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { formatInteger } from '../../../../apps/standalone/src/utils/format-integer';

import type { FetchableResourceType } from '@ctw/shared/context/_internal/resource/resource-mappings';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';

export const clinicalObjectStatuses = ['open', 'closed'] as const;
export type ClinicalObjectStatus = (typeof clinicalObjectStatuses)[number];

const clinicalObjectStatusIconMap: Record<ClinicalObjectStatus, IconDefinition> = {
  open: faClock,
  closed: faCircleX,
} as const;

interface ClinicalObjectTag extends Pick<BadgeProps, 'variant'> {
  label: string;
}

interface ClinicalObjectDescriptorStaticText {
  label?: never;
  icon?: never;
  content: ReactNode;
  quantity?: number;
  collapsible?: never;
}

interface ClinicalObjectDescriptorCollapsibleText {
  label: string;
  icon: IconDefinition;
  content: ReactNode;
  quantity?: number;
  collapsible: true;
}

export const isClinicalObjectDescriptorStaticText = (
  descriptor: ClinicalObjectDescriptor,
): descriptor is ClinicalObjectDescriptorStaticText => {
  return descriptor.collapsible === undefined;
};

type ClinicalObjectDescriptor =
  | ClinicalObjectDescriptorStaticText
  | ClinicalObjectDescriptorCollapsibleText;

interface ClinicalObjectDeepLink {
  primaryAction: 'open_link' | 'copy_link';
  resourceType: FetchableResourceType;
  resourceId: string;
}

export interface ClinicalObjectCardProps extends PropsWithChildren {
  label?: string;
  title: string;
  tags?: Readonly<Array<ClinicalObjectTag>>;
  status?: ClinicalObjectStatus;
  descriptors?: Readonly<Array<ClinicalObjectDescriptor>>;
  grouped?: boolean;
  selectable?: boolean;
  deepLinkTo?: ClinicalObjectDeepLink;
  telemetryContext?: Record<string, unknown>;
  onClick?: () => void;
  onSelected?: () => void;
  onDeselected?: () => void;
}

export const ClinicalObjectCard = ({
  label,
  title,
  status,
  tags = [],
  descriptors = [],
  grouped = false,
  selectable = false,
  deepLinkTo,
  onClick,
  onSelected,
  onDeselected,
  telemetryContext,
  children,
}: ClinicalObjectCardProps) => {
  const telemetry = useTelemetry();
  const [selected, internalSetSelected] = useState(false);
  const deepLinkActionRef = useRef<HTMLButtonElement>(null);

  const hasPrimaryAction = useMemo(
    () => selectable || deepLinkTo !== undefined || onClick !== undefined,
    [selectable, deepLinkTo, onClick],
  );

  const toggleSelected = useCallback(() => {
    internalSetSelected((current) => {
      if (current) {
        telemetry.trackInteraction('clinical_object_card_deselected', telemetryContext);

        onDeselected?.();
      } else {
        telemetry.trackInteraction('clinical_object_card_selected', telemetryContext);
        onSelected?.();
      }

      return !current;
    });
  }, [onSelected, onDeselected, telemetry, telemetryContext]);

  const contentWrapperRole = useMemo(() => {
    if (selectable) {
      return 'gridcell';
    }

    if (onClick) {
      return 'button';
    }

    if (deepLinkTo) {
      return 'link';
    }

    return undefined;
  }, [selectable, deepLinkTo, onClick]);

  const performPrimaryCardAction = useCallback(() => {
    if (selectable) {
      toggleSelected();
    } else if (onClick) {
      telemetry.trackInteraction('clinical_object_card_primary_action', {
        ...telemetryContext,
        clinicalObjectCardPrimaryActionType: 'button',
      });
      onClick();
    } else if (deepLinkTo !== undefined && deepLinkActionRef.current) {
      telemetry.trackInteraction('clinical_object_card_primary_action', {
        ...telemetryContext,
        clinicalObjectCardPrimaryActionType: 'link',
      });
      const syntheticClickEvent = new CustomEvent('click', {
        bubbles: true,
        cancelable: true,
        detail: {
          action: deepLinkTo.primaryAction,
        },
      });
      deepLinkActionRef.current.dispatchEvent(syntheticClickEvent);
    }
  }, [selectable, deepLinkTo, onClick, toggleSelected, telemetry, telemetryContext]);

  return (
    <section
      className={twx(
        'group/card flex flex-col overflow-visible rounded-md border border-border-main bg-white shadow-sm',
        {
          'hover:border-content-subtle/35 hover:shadow-md':
            !grouped && (selectable || deepLinkTo !== undefined),
          'rounded-none border-none': grouped,
          'shadow-md': selected,
        },
      )}
      role={grouped ? 'grid' : undefined}
    >
      {/* biome-ignore lint/nursery/noStaticElementInteractions: Required for complex interaction */}
      <div
        className={twx(
          'flex h-full w-full cursor-default flex-col gap-1 rounded-md p-3 focus-visible:outline-4 focus-visible:outline-primary-main',
          {
            'cursor-pointer hover:bg-background-hover': hasPrimaryAction,
            'bg-background-hover': selected,
          },
        )}
        tabIndex={hasPrimaryAction ? 0 : undefined}
        role={contentWrapperRole}
        aria-label={title}
        aria-selected={selected}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();

          performPrimaryCardAction();
        }}
        onKeyDown={(event) => {
          event.preventDefault();
          event.stopPropagation();

          if (['Enter', ' '].includes(event.key)) {
            performPrimaryCardAction();
          }
        }}
      >
        <header className={tw`flex flex-col`}>
          <div
            className={twx('flex flex-wrap justify-between gap-2', {
              'flex-wrap-reverse items-end': isEmpty(label),
            })}
          >
            <div
              className={twx('flex max-h-min flex-col items-start gap-1.5', {
                'min-w-[40%] grow basis-[40%]': isEmpty(label),
                'min-w-[30%] grow basis-[30%]': !isEmpty(label),
              })}
            >
              {isEmpty(label) ? (
                <div className={tw`flex min-h-[22px] items-center`}>
                  <span
                    className={twx(
                      'line-clamp-2 font-medium text-content-main text-sm leading-tight',
                      {
                        'decoration-content-main group-hover/card:underline': hasPrimaryAction,
                      },
                    )}
                  >
                    {title}
                  </span>
                </div>
              ) : (
                <div className={tw`flex min-h-[22px] items-center`}>
                  <span
                    className={tw`overflow-hidden text-ellipsis whitespace-nowrap font-medium text-content-subtle text-sm uppercase leading-none`}
                  >
                    {label}
                  </span>
                </div>
              )}
            </div>
            <div className={tw`flex max-h-min min-h-[22px] items-center justify-start gap-1.5`}>
              {(status || tags.length > 0) && (
                <div className={tw`flex flex-wrap items-center justify-start gap-1`}>
                  {tags.map((tag) => (
                    <Badge key={tag.label} content={tag.label} variant={tag.variant} size="sm" />
                  ))}
                  {status && (
                    <div className={tw`flex items-center justify-center gap-1`}>
                      <FontAwesomeIcon
                        icon={clinicalObjectStatusIconMap[status]}
                        className={twx('h-3.5 w-3.5 text-content-subtle', {
                          'text-success-main': status === 'open',
                          'text-content-subtle': status === 'closed',
                        })}
                      />
                      <div
                        className={twx('whitespace-nowrap font-medium text-xs uppercase', {
                          'text-success-main': status === 'open',
                          'text-content-subtle': status === 'closed',
                        })}
                      >
                        {status}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {!(isEmpty(label) || isEmpty(title)) && (
            <div className={tw`flex min-h-[22px] items-center`}>
              <span
                className={twx('line-clamp-2 font-medium text-content-main text-sm leading-tight', {
                  'decoration-content-main group-hover/card:underline': hasPrimaryAction,
                })}
              >
                {title}
              </span>
            </div>
          )}
        </header>
        {children && <div className={tw`font-normal text-content-main text-sm`}>{children}</div>}
        {(descriptors.length > 0 || deepLinkTo !== undefined) && (
          <footer className={tw`@container/footer flex max-h-min items-end justify-between gap-1`}>
            <ul className={tw`flex min-h-[25px] basis-2/3 items-center gap-1.5`}>
              {descriptors.map((descriptor) => (
                <li key={descriptor.label}>
                  <ClinicalObjectDescriptor descriptor={descriptor} />
                </li>
              ))}
            </ul>
            <div
              className={tw`flex min-h-[25px] max-w-min basis-1/3 items-center justify-end gap-0.5`}
            >
              {deepLinkTo && (
                <ClinicalObjectDeepLinkAction ref={deepLinkActionRef} deepLinkTo={deepLinkTo} />
              )}
              {selectable && (
                <div className={twx('pointer-events-none relative h-[25px] w-[25px]')}>
                  <FontAwesomeIcon
                    icon={faSquare}
                    className={twx(
                      'absolute top-0 right-0 bottom-0 left-0 m-auto block h-5 w-5 text-border-main opacity-0 transition-all duration-200',
                      {
                        'opacity-100': !selected,
                      },
                    )}
                  />
                  <FontAwesomeIcon
                    icon={faSquareCheck}
                    className={twx(
                      'absolute top-0 right-0 bottom-0 left-0 m-auto block h-5 w-5 text-success-main opacity-0 transition-all duration-200',
                      {
                        'opacity-100': selected,
                      },
                    )}
                  />
                </div>
              )}
            </div>
          </footer>
        )}
      </div>
    </section>
  );
};

interface ClinicalObjectDeepLinkActionProps {
  deepLinkTo: ClinicalObjectDeepLink;
  ref?: RefObject<HTMLButtonElement | null>;
}

const ClinicalObjectDeepLinkAction = ({ deepLinkTo, ref }: ClinicalObjectDeepLinkActionProps) => {
  const { navigate } = useZuiContext();

  return (
    <ClinicalObjectActionButton
      ref={ref}
      label="Copy Link to Details"
      icon={faLink}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();

        const deepLinkUrl = `${window.location.protocol}//${window.location.host}${pathWithParameters(
          window.location.pathname,
          {
            resourceType: deepLinkTo.resourceType,
            resourceId: deepLinkTo.resourceId,
          },
        )}`;

        if (
          (event.detail as unknown as Record<string, string> | undefined)?.action === 'open_link'
        ) {
          navigate(deepLinkUrl);
        } else {
          void copyToClipboard(deepLinkUrl, () => {
            notify({
              type: 'info',
              title: `Copied to clipboard: '${deepLinkUrl}'`,
            });
          });
        }
      }}
    />
  );
};

interface ClinicalObjectActionButtonProps {
  label: string;
  icon: IconDefinition;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  ref?: RefObject<HTMLButtonElement | null>;
}

const ClinicalObjectActionButton = ({
  label,
  icon,
  onClick,
  ref,
}: ClinicalObjectActionButtonProps) => {
  return (
    <Tooltip
      id={`clinical-object-action-button-${snakeCase(label)}`}
      place="top"
      content={label}
      contentClassName={tw`text-xs font-medium tracking-wide`}
    >
      <Button
        ref={ref}
        className={tw`active:!scale-[.90] flex aspect-square items-center justify-center rounded-full p-1 drop-shadow-sm transition-all duration-150 hover:bg-background-subtle hover:shadow-sm`}
        type="button"
        variant="unstyled"
        onClick={onClick}
        ariaLabel={label}
      >
        <FontAwesomeIcon icon={icon} className={tw`h-[17px] w-[17px] text-content-icon`} />
      </Button>
    </Tooltip>
  );
};

interface ClinicalObjectDescriptorProps {
  descriptor: ClinicalObjectDescriptor;
}

const ClinicalObjectDescriptor = ({ descriptor }: ClinicalObjectDescriptorProps) => {
  if (isClinicalObjectDescriptorStaticText(descriptor)) {
    return <div className={'text-content-subtle text-sm'}>{descriptor.content}</div>;
  }

  return (
    <Tooltip
      id={`clinical-object-descriptor-${snakeCase(descriptor.label)}`}
      place="top"
      content={
        <div className={tw`flex flex-col items-center justify-center`}>
          <div className={tw`flex items-center justify-center gap-1`}>
            <FontAwesomeIcon icon={descriptor.icon} className={tw`h-[17px] w-[17px] text-white`} />

            <div
              className={tw`h-[1.15rem] max-h-[1.15rem] min-h-[1.15rem] font-medium text-sm leading-[1.15rem] tracking-wide`}
            >
              {descriptor.label}{' '}
              {descriptor.quantity !== undefined
                ? `(${formatInteger(descriptor.quantity)})`
                : undefined}
            </div>
          </div>
          <HorizontalDivider className={tw`mt-1 mb-3 border-white/25`} />
          <div className={tw`mb-2`}>{descriptor.content}</div>
        </div>
      }
      className={tw`group/descriptor flex h-[17px] min-w-[17px] items-center justify-center gap-1`}
      contentClassName={tw`text-xs font-medium tracking-wide`}
    >
      <div className={tw`flex items-center gap-0.5`}>
        <FontAwesomeIcon
          icon={descriptor.icon}
          className={tw`h-[17px] w-[17px] text-content-icon drop-shadow-sm transition-all duration-200 group-hover/descriptor:text-content-subtle`}
        />

        {descriptor.quantity !== undefined && (
          <span
            className={tw`flex h-[0.9rem] max-h-[0.9rem] min-h-[0.9rem] w-max min-w-max items-start whitespace-nowrap font-medium text-content-icon text-xs leading-[0.9rem] drop-shadow-sm transition-all duration-200 group-hover/descriptor:text-content-subtle`}
          >
            {formatInteger(descriptor.quantity)}
          </span>
        )}
      </div>
    </Tooltip>
  );
};
